import { Modal, Button } from '@distribusion/owl-frontend-ui'
import { Field, Form, Formik } from 'formik'
import React from 'react'

import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import { email, required, chain } from '@lib/validators'
import { useResetPassword } from '@loaders/resetPassword'
import { ModalType, MODAL_TYPES } from '@pages/Account/hooks/usePasswordChange'
import { useParams } from '@stores/params'
import InputField from '@ui/Input/Field'

import '@pages/Account/Settings/Modals/ForgotPasswordModal/index.scss'

interface ForgotPasswordProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (email: string) => void
  onError?: (errorType: ModalType) => void
  initialEmail?: string
}

interface ForgotPasswordFormValues {
  email: string
}

export const ForgotPasswordModal = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  initialEmail = '',
}: ForgotPasswordProps) => {
  const { t } = useTranslation()
  const [{ retailerPartnerNumber }] = useParams()
  const { mutateAsync: resetPassword, isLoading } = useResetPassword()

  const handleSubmit = async (values: ForgotPasswordFormValues) => {
    try {
      await resetPassword({ email: values.email, retailerPartnerNumber: Number(retailerPartnerNumber) })
      onClose()
      onSuccess?.(values.email)
    } catch {
      onError?.(MODAL_TYPES.NO_USER)
    }
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={t('account.passwordRecovery.forgotModal.title')}
      maxWidth="sm"
      dataTag="forgot-password-modal"
    >
      <Formik<ForgotPasswordFormValues>
        initialValues={{ email: initialEmail }}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        <Form>
          <div className={bem('forgot-modal', 'content')}>
            <span> {t('account.passwordRecovery.forgotModal.description')}</span>
            <Field
              component={InputField}
              label={t('account.passwordRecovery.forgotModal.email')}
              name="email"
              validate={chain([required, email])}
              required
              dataTag="reset-email-input"
            />
            <Button
              color="primary"
              className={bem('forgot-modal', 'button')}
              buttonType="submit"
              loading={isLoading}
              dataTag="submit-password-reset"
            >
              {t('account.passwordRecovery.forgotModal.button')}
            </Button>
          </div>
        </Form>
      </Formik>
    </Modal>
  )
}
